import React from 'react';
import PropTypes from 'prop-types';
// @mui
import {
    Box,
    Checkbox,
    TableRow,
    TableCell,
    TableHead,
    TableSortLabel,
    Tooltip
} from '@mui/material';

import i18n from '../../../i18n';
import { isFieldVisibleByRole } from '../../../utils/isFieldVisible';
import {
    columnAlign,
    GRID_ACTIONS_CELL_PROPS
} from '../../../utils/gridColumns';
import { stickyActionHeadCellSx } from '../../../components/tableStyles';

// ----------------------------------------------------------------------

const visuallyHidden = {
    border: 0,
    margin: -1,
    padding: 0,
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    position: 'absolute',
    whiteSpace: 'nowrap',
    clip: 'rect(0 0 0 0)'
};

GridListHead.propTypes = {
    order: PropTypes.oneOf(['asc', 'desc']),
    orderBy: PropTypes.string,
    rowCount: PropTypes.number,
    headLabel: PropTypes.array,
    numSelected: PropTypes.number,
    onRequestSort: PropTypes.func,
    onSelectAllClick: PropTypes.func,
    /** Whether the rows carry the sticky ⋮ column. */
    menuColumn: PropTypes.bool,
    /** `(headCell) => node` placed in each column's head cell: the drag grip
     * and the resize edge. */
    renderColumnHandle: PropTypes.func,
    /** `(headCell) => props` spread on each column's head cell, for dragging
     * it to another place. */
    getColumnDragProps: PropTypes.func,
    /** Controls for the head cell of the actions column: the columns button. */
    actions: PropTypes.node
};

// The field a column sorts the list by. Also the phone's sort sheet, which
// has no heads to tap.
export const gridSortLabel = (field) => {
    if (field.customSort) {
        return field.customSort;
    }
    if (field.show && field.object) {
        return `${field.object}.${field.show}`;
    }
    return field.id;
};

export default function GridListHead({
    order,
    orderBy,
    rowCount,
    headLabel,
    numSelected,
    onRequestSort,
    onSelectAllClick,
    checkboxCell,
    role,
    menuColumn = true,
    renderColumnHandle,
    getColumnDragProps,
    actions
    // rows
}) {
    const createSortHandler = (property) => () => {
        onRequestSort(property);
    };
    // const visibleColumns = getVisibleColumns(headLabel, rows);
    // console.log('visibleColumns', visibleColumns);
    return (
        <TableHead>
            <TableRow>
                {checkboxCell && (
                    <TableCell key="checkbox" padding="checkbox">
                        <Checkbox
                            indeterminate={
                                numSelected > 0 && numSelected < rowCount
                            }
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={onSelectAllClick}
                            inputProps={{ 'aria-label': i18n.t('common.selectAll') }}
                        />
                    </TableCell>
                )}
                {headLabel
                    .filter((f) => {
                        const fieldKey =
                            f.cell === 'SelectObject' ? f.object : f.id;
                        return (
                            f.displayGrid &&
                            isFieldVisibleByRole(fieldKey, role)
                        );
                    })
                    .map((headCell) => {
                        const sortLabel = gridSortLabel(headCell);
                        const headCellText = headCell.labelCell ? i18n.t(`columns.${headCell.labelCell}`) : i18n.t(`columns.${headCell.label}`);
                        // A column's `hint`, a whole translation key, is its
                        // header's tooltip: what the figures under it mean (a
                        // gateway's balance is negative when prepaid), where
                        // the label alone would not say.
                        const headCellLabel = headCell.hint ? (
                            <Tooltip title={i18n.t(headCell.hint)}>
                                <Box
                                    component="span"
                                    sx={{ textDecoration: 'underline dotted', cursor: 'help' }}
                                >
                                    {headCellText}
                                </Box>
                            </Tooltip>
                        ) : (
                            headCellText
                        );
                        return (
                            <TableCell
                                key={headCell.id}
                                align={columnAlign(headCell)}
                                sortDirection={
                                    !headCell.disableSort &&
                                    orderBy === sortLabel
                                        ? order
                                        : false
                                }
                                data-col-id={headCell.id}
                                {...getColumnDragProps?.(headCell)}
                            >
                                {headCell.disableSort ? (
                                    headCellLabel
                                ) : (
                                    <TableSortLabel
                                        hideSortIcon
                                        active={orderBy === sortLabel}
                                        direction={
                                            orderBy === sortLabel
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler(sortLabel)}
                                        // On a right-aligned column the sort
                                        // arrow would otherwise sit outside the
                                        // label and push it off the right edge.
                                        sx={
                                            columnAlign(headCell) === 'right'
                                                ? { flexDirection: 'row-reverse' }
                                                : undefined
                                        }
                                    >
                                        {headCellLabel}
                                        {orderBy === sortLabel && (
                                            <Box sx={{ ...visuallyHidden }}>
                                                {order === 'desc'
                                                    ? 'sorted descending'
                                                    : 'sorted ascending'}
                                            </Box>
                                        )}
                                    </TableSortLabel>
                                )}
                                {renderColumnHandle?.(headCell)}
                            </TableCell>
                        );
                    })}
                {menuColumn && (
                    <TableCell
                        key="menu"
                        align="center"
                        sx={stickyActionHeadCellSx}
                        {...GRID_ACTIONS_CELL_PROPS}
                    >
                        {actions}
                    </TableCell>
                )}
            </TableRow>
        </TableHead>
    );
}
